/**
 * Daily Photo List data table.
 *
 * Renders vehicles for a single sheet tab with Excel-matching styling:
 *   - Per-sheet column sets (NEW default, Toyota NEW, Dodge/Fernley NEW, USED)
 *   - Zebra striping (white / light grey)
 *   - Highlight rules: recon status colors, STOP red/bold, transit colors,
 *     stargard green, PARTIAL blue (always wins)
 *   - Title row with sheet name + date
 *   - Sticky header row
 *
 * On mobile (useIsMobile) each vehicle renders as a stacked card instead —
 * the wholesale ClearedCardList lineage — with the row's winning Excel
 * highlight painted on the card's left edge and per-metric pills, both driven
 * by the same getCellStyle rules as the table, so the color language is
 * identical across phone, desktop, and workbook.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { setVehicleProgress, type ProgressStatus } from "../../api/photoList"
import type { PhotoListVehicle } from "../../api/photoList"
import {
  HAND_ADDED_NOTES_MARK,
  getColumnsForSheet,
  type PhotoListColumn,
} from "../../config/photoListColumns"
import { tint } from "../../utils/color"
import { getCellStyle } from "../../utils/photoListStyling"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"
import ProgressCircles from "./ProgressCircles"
import VehicleNotesModal from "./VehicleNotesModal"

interface Props {
  vehicles: PhotoListVehicle[]
  sheetSlug: string
  sheetLabel: string
  runDate: string | null
  /** Active dealer group — scopes the notes thread; passed down from the page. */
  dealerGroupId: number | null
  /** Overrides the empty-state text (e.g. "no vehicles match the search"). */
  emptyMessage?: string
  /** Removes a hand-added car — passed only while the page shows today's
   * list, so an older run's hand-added cars offer no Remove. */
  onRemoveAddedVehicle?: (vehicle: PhotoListVehicle) => Promise<void>
}

function formatTitleDate(isoDate: string | null): string {
  if (!isoDate) return ""
  const d = new Date(isoDate)
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: "America/Los_Angeles",
  })
}

function formatCellValue(col: PhotoListColumn, value: unknown): string {
  if (value === null || value === undefined || value === "") return "\u2014"
  if (col.key === "age_days" && typeof value === "number") {
    return value.toLocaleString()
  }
  return String(value)
}

export default function PhotoListTable({
  vehicles,
  sheetSlug,
  sheetLabel,
  runDate,
  dealerGroupId,
  emptyMessage,
  onRemoveAddedVehicle,
}: Props) {
  const columns = useMemo(() => getColumnsForSheet(sheetSlug), [sheetSlug])
  const totalWidth = useMemo(
    () => columns.reduce((sum, c) => sum + c.width, 0),
    [columns],
  )
  const isMobile = useIsMobile()

  // Notes modal state. Overrides let a just-written note repaint its Notes
  // cell immediately without refetching the sheet — keyed by VIN, so the
  // override survives sheet switches (same car, same thread).
  const [notesVehicle, setNotesVehicle] = useState<PhotoListVehicle | null>(null)
  const [noteOverrides, setNoteOverrides] = useState<Record<string, string>>({})

  const latestNoteFor = useCallback(
    (vehicle: PhotoListVehicle): string | null =>
      (vehicle.vin ? noteOverrides[vehicle.vin] : undefined) ?? vehicle.latest_note,
    [noteOverrides],
  )

  const handleNoteAdded = useCallback((vin: string, body: string) => {
    setNoteOverrides((prev) => ({ ...prev, [vin]: body }))
  }, [])

  // Five-circle progress (mobile tiles + the notes modal). Optimistic: the
  // tap paints immediately — a photographer in the lane shouldn't wait on the
  // network to see their own tap. A ref mirrors the override map so the tap
  // handler keeps a stable identity (memoized cards depend on that), and
  // saves are chained per VIN so rapid taps can't land out of order
  // server-side.
  const [progressOverrides, setProgressOverrides] = useState<
    Record<string, ProgressStatus | null>
  >({})
  const [progressError, setProgressError] = useState<string | null>(null)
  const progressOverridesRef = useRef(progressOverrides)
  const progressSaveChains = useRef<Record<string, Promise<void>>>({})

  // Group A's VIN-keyed overrides must never paint group B's rows, so both
  // maps reset on a dealer-group switch. They deliberately SURVIVE sheet
  // switches: the context serves each sheet from a cache keyed runId:sheet,
  // so returning to a sheet replays a snapshot fetched BEFORE the tap — the
  // override is what bridges that stale cache to the state the server
  // already holds. (Clearing on every vehicles change made a saved tap look
  // lost on sheet-hop-and-return — Ian hit it on 2026-09-01.)
  useEffect(() => {
    progressOverridesRef.current = {}
    setProgressOverrides({})
    setNoteOverrides({})
  }, [dealerGroupId])

  const progressFor = useCallback(
    (vehicle: PhotoListVehicle): ProgressStatus | null =>
      vehicle.vin && vehicle.vin in progressOverrides
        ? progressOverrides[vehicle.vin]
        : vehicle.progress_status,
    [progressOverrides],
  )

  const handleProgressTap = useCallback(
    (vehicle: PhotoListVehicle, status: ProgressStatus) => {
      const vin = vehicle.vin
      if (!vin) return
      const overrides = progressOverridesRef.current
      const previous = vin in overrides ? overrides[vin] : vehicle.progress_status
      // Tapping the active circle clears the state.
      const next = previous === status ? null : status
      progressOverridesRef.current = { ...overrides, [vin]: next }
      setProgressOverrides(progressOverridesRef.current)
      setProgressError(null)
      // Serialize saves per car: each tap waits for the previous one, so the
      // server applies them in tap order and the last tap wins.
      const chain = progressSaveChains.current[vin] ?? Promise.resolve()
      progressSaveChains.current[vin] = chain.then(() =>
        setVehicleProgress(vin, next, vehicle.stock, dealerGroupId).catch(() => {
          // Fall back to the server's value rather than guessing which of
          // the queued taps survived.
          const current = { ...progressOverridesRef.current }
          delete current[vin]
          progressOverridesRef.current = current
          setProgressOverrides(current)
          setProgressError(
            "Couldn't save the status — check your connection and try again.",
          )
        }),
      )
    },
    [dealerGroupId],
  )

  // The modal carries the same circle row as the tiles (Ian, 2026-09-10) —
  // progress comes through progressFor, so a tap in the modal repaints the
  // card behind it, and vice versa. On desktop the modal is the ONLY circle
  // surface (the table has no circle column).
  const notesModal = notesVehicle && (
    <VehicleNotesModal
      vehicle={notesVehicle}
      dealerGroupId={dealerGroupId}
      progress={progressFor(notesVehicle)}
      onProgressTap={(status) => handleProgressTap(notesVehicle, status)}
      onClose={() => setNotesVehicle(null)}
      onNoteAdded={handleNoteAdded}
      onRemove={
        notesVehicle.added_by_hand && onRemoveAddedVehicle
          ? () => onRemoveAddedVehicle(notesVehicle)
          : undefined
      }
    />
  )

  if (vehicles.length === 0) {
    return (
      <div
        style={{
          padding: THEME.spacing.xl,
          textAlign: "center",
          color: THEME.colors.neutral.light.textSecondary,
          fontSize: 15,
        }}
      >
        {emptyMessage ?? "No vehicles in this sheet."}
      </div>
    )
  }

  // On a phone the ~1,200px table is unusable scrolled sideways — render each
  // vehicle as a stacked card instead (mirrors the wholesale ClearedCardList,
  // itself the Image Index card pattern).
  if (isMobile) {
    return (
      <>
        {progressError && <div style={progressErrorStyle}>{progressError}</div>}
        <PhotoListCardList
          vehicles={vehicles}
          sheetSlug={sheetSlug}
          sheetLabel={sheetLabel}
          runDate={runDate}
          latestNoteFor={latestNoteFor}
          onOpenNotes={setNotesVehicle}
          progressFor={progressFor}
          onProgressTap={handleProgressTap}
        />
        {notesModal}
      </>
    )
  }

  return (
    <>
    {/* Desktop taps circles through the notes modal — its save errors need a
        home on this branch too. */}
    {progressError && <div style={progressErrorStyle}>{progressError}</div>}
    <div
      style={{
        overflowX: "auto",
        border: `1px solid ${THEME.auditTable.borderColor}`,
        borderRadius: THEME.borderRadius.md,
      }}
    >
      <table
        style={{
          borderCollapse: "separate",
          borderSpacing: 0,
          width: "100%",
          minWidth: totalWidth,
          tableLayout: "fixed",
          fontSize: 13,
          fontFamily: THEME.fonts.primary,
        }}
      >
        <colgroup>
          {columns.map((col) => (
            <col key={col.key} style={{ width: col.width }} />
          ))}
        </colgroup>

        {/* Title row */}
        <thead>
          <tr>
            <th
              colSpan={columns.length}
              style={{
                backgroundColor: THEME.auditTable.titleBarBackground,
                color: THEME.auditTable.titleBarText,
                padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                fontSize: 16,
                fontWeight: 600,
                textAlign: "left",
                position: "sticky",
                top: 0,
                zIndex: 3,
              }}
            >
              {sheetLabel}
              {runDate && (
                <span style={{ fontWeight: 400, marginLeft: 12, fontSize: 14 }}>
                  {formatTitleDate(runDate)}
                </span>
              )}
              <span
                style={{
                  float: "right",
                  fontWeight: 400,
                  fontSize: 13,
                  opacity: 0.85,
                }}
              >
                {vehicles.length.toLocaleString()} vehicle{vehicles.length !== 1 ? "s" : ""}
              </span>
            </th>
          </tr>

          {/* Header row */}
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  backgroundColor: THEME.auditTable.headerBackground,
                  color: THEME.colors.white,
                  padding: `6px ${THEME.spacing.sm}`,
                  fontWeight: 500,
                  fontSize: 12,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  textAlign: col.align ?? "left",
                  borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
                  borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                  position: "sticky",
                  top: 35,
                  zIndex: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  userSelect: "none",
                }}
                title={col.header}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {vehicles.map((vehicle, rowIdx) => {
            const zebraColor =
              rowIdx % 2 === 0
                ? THEME.auditTable.zebraLight
                : THEME.auditTable.zebraMain
            return (
              <tr key={vehicle.id} style={{ backgroundColor: zebraColor }}>
                {columns.map((col) => {
                  // The Notes column shows the car's newest operator note and
                  // opens the notes modal. The Excel export paints the same
                  // note (Ian, 2026-09-10); only the print view keeps the
                  // column blank for hand-writing.
                  // Notes key on the VIN \u2014 a VIN-less row can't carry one, so
                  // it gets no click target.
                  const isNotesColumn = col.key === "print_notes" && Boolean(vehicle.vin)
                  const raw = vehicle[col.key]
                  const display = isNotesColumn
                    ? latestNoteFor(vehicle) ?? "\u2014"
                    : formatCellValue(col, raw)
                  // getCellStyle() returns data-driven styling (background,
                  // text color, weight) per the photo list parity rules.
                  // Spread it LAST so any data colors override our chrome
                  // defaults \u2014 never the other way around.
                  const cellStyle = getCellStyle(vehicle, col.key)
                  return (
                    <td
                      key={col.key}
                      onClick={isNotesColumn ? () => setNotesVehicle(vehicle) : undefined}
                      title={
                        isNotesColumn
                          ? "View / add notes"
                          : display !== "\u2014"
                            ? display
                            : undefined
                      }
                      style={{
                        padding: `4px ${THEME.spacing.sm}`,
                        color: THEME.colors.neutral.light.textPrimary,
                        borderRight: `1px solid ${THEME.auditTable.borderColor}`,
                        textAlign: col.align ?? "left",
                        fontFamily: col.mono ? THEME.fonts.mono : undefined,
                        fontSize: col.mono ? 12 : undefined,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        ...(isNotesColumn && { cursor: "pointer" }),
                        ...cellStyle,
                      }}
                    >
                      {/* A hand-added car's Notes cell leads with ADDED —
                          the column Ian picked for the Excel mark. */}
                      {isNotesColumn && vehicle.added_by_hand ? (
                        <>
                          <span style={addedChipStyle}>{HAND_ADDED_NOTES_MARK}</span>
                          {latestNoteFor(vehicle) && ` ${latestNoteFor(vehicle)}`}
                        </>
                      ) : (
                        display
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
    {notesModal}
    </>
  )
}

// ---------------------------------------------------------------------------
// Mobile card list — stacked cards, one per vehicle. Cite the wholesale
// ClearedCardList (components/wholesale/MatchResultsTable.tsx, ~line 449)
// which itself mirrors the Adesa Run List's and Image Index's cards.
//
// Excel-parity color mapping (Principle 4 — same rules, no second copy):
//   - Card left edge = the row's WINNING highlight. Every highlight rule in
//     utils/photoListStyling.ts paints the "vin" column, so the VIN cell's
//     background IS the row color under the exact Excel precedence
//     (PARTIAL blue always last/wins).
//   - Metric values re-run getCellStyle for their own column, so e.g. a
//     Transit value shows its transit color even when the row edge is green.
// ---------------------------------------------------------------------------

// Card metrics are the sheet's own status-ish columns (from the mirror
// config), so a sheet-specific column like Toyota's STOP or Dodge/Fernley's
// Transit appears exactly where the sheet defines it. Identity fields
// (year/make/model/trim/color, VIN, stock, notes) and the shot-status chip
// have their own card slots — color rides on the car-name line (Ian,
// 2026-10-11). No highlight rule can paint color, so it loses no cell color
// by leaving the metrics row.
const CARD_METRIC_KEYS = new Set<keyof PhotoListVehicle>([
  "age_days",
  "sale_status_raw",
  "recon_status_raw",
  "cmpgn",
  "transit",
  "stargard",
])

interface CardListProps {
  vehicles: PhotoListVehicle[]
  sheetSlug: string
  sheetLabel: string
  runDate: string | null
  latestNoteFor: (vehicle: PhotoListVehicle) => string | null
  onOpenNotes: (vehicle: PhotoListVehicle) => void
  progressFor: (vehicle: PhotoListVehicle) => ProgressStatus | null
  onProgressTap: (vehicle: PhotoListVehicle, status: ProgressStatus) => void
}

function PhotoListCardList({
  vehicles,
  sheetSlug,
  sheetLabel,
  runDate,
  latestNoteFor,
  onOpenNotes,
  progressFor,
  onProgressTap,
}: CardListProps) {
  // Stable reference so the memoized cards don't see a fresh array per render.
  const metricColumns = useMemo(
    () => getColumnsForSheet(sheetSlug).filter((col) => CARD_METRIC_KEYS.has(col.key)),
    [sheetSlug],
  )

  return (
    <div style={cardListStyle}>
      {/* Compact stand-in for the table's title row: sheet + date + count */}
      <div style={cardListHeaderStyle}>
        <span style={{ fontWeight: 600, color: THEME.colors.neutral.light.textPrimary }}>
          {sheetLabel}
        </span>
        {runDate && <span>{formatTitleDate(runDate)}</span>}
        <span style={{ marginLeft: "auto" }}>
          {vehicles.length.toLocaleString()} vehicle{vehicles.length !== 1 ? "s" : ""}
        </span>
      </div>

      {vehicles.map((vehicle) => (
        <VehicleCard
          key={vehicle.id}
          vehicle={vehicle}
          metricColumns={metricColumns}
          latestNote={latestNoteFor(vehicle)}
          onOpenNotes={onOpenNotes}
          progress={progressFor(vehicle)}
          onProgressTap={onProgressTap}
        />
      ))}
    </div>
  )
}

// Memoized so one circle tap re-renders only the tapped card, not the whole
// sheet: props are primitives plus stable handler identities (the handlers
// take the vehicle as an argument instead of closing over it).
const VehicleCard = React.memo(function VehicleCard({
  vehicle,
  metricColumns,
  latestNote,
  onOpenNotes,
  progress,
  onProgressTap,
}: {
  vehicle: PhotoListVehicle
  metricColumns: PhotoListColumn[]
  latestNote: string | null
  onOpenNotes: (vehicle: PhotoListVehicle) => void
  progress: ProgressStatus | null
  onProgressTap: (vehicle: PhotoListVehicle, status: ProgressStatus) => void
}) {
  const rowHighlight = getCellStyle(vehicle, "vin").backgroundColor
  const shotChipBackground = getCellStyle(vehicle, "shot_status").backgroundColor

  const carName =
    [vehicle.year != null ? String(vehicle.year) : null, vehicle.make, vehicle.model]
      .filter(Boolean)
      .join(" ") || "Vehicle"
  // Color shares the car-name line (Ian, 2026-10-11); a car with no color
  // shows just its name rather than a trailing dash.
  const title = vehicle.color_ext ? `${carName} · ${vehicle.color_ext}` : carName
  // Stock and VIN share one mono identity line (Ian, 2026-09-01), kept free
  // of anything else so it never truncates the VIN.
  const identityLine = [
    vehicle.stock ? `Stock ${vehicle.stock}` : null,
    vehicle.vin ? `VIN ${vehicle.vin}` : null,
  ]
    .filter(Boolean)
    .join("  ·  ")

  return (
    <div style={cardStyle(rowHighlight)}>
      {/* Stock + VIN lead the card, above the car name (Ian, 2026-10-11). */}
      {identityLine && <div style={cardIdentityStyle}>{identityLine}</div>}
      <div style={cardTitleRowStyle}>
        <span style={cardTitleStyle}>{title}</span>
        {vehicle.added_by_hand && <span style={addedChipStyle}>{HAND_ADDED_NOTES_MARK}</span>}
        {vehicle.shot_status && (
          <span style={shotStatusChipStyle(shotChipBackground)}>{vehicle.shot_status}</span>
        )}
      </div>
      <div style={cardSummaryStyle}>
        {metricColumns.map((col) => (
          <CardMetric
            key={col.key}
            label={col.header}
            value={formatCellValue(col, vehicle[col.key])}
            cellStyle={getCellStyle(vehicle, col.key)}
          />
        ))}
      </div>
      {/* Five-circle progress — the photographer's workflow states (Ian):
          no keys / in progress / issue / cancelled / done. Tap to set, tap
          the active one to clear. VIN-less rows can't key a state. */}
      {vehicle.vin && (
        <ProgressCircles
          active={progress}
          onTap={(status) => onProgressTap(vehicle, status)}
        />
      )}

      {/* Notes line — always present so the tap target is discoverable.
          Hidden for VIN-less rows: notes key on the VIN, so there is nothing
          to attach one to. */}
      {vehicle.vin && (
        <div style={cardNotesRowStyle} onClick={() => onOpenNotes(vehicle)}>
          {latestNote ? (
            <span style={cardNotesStyle}>{latestNote}</span>
          ) : (
            <span style={cardAddNoteStyle}>Add note…</span>
          )}
        </div>
      )}
    </div>
  )
})

function CardMetric({
  label,
  value,
  cellStyle,
}: {
  label: string
  value: string
  cellStyle: React.CSSProperties
}) {
  // A colored cell becomes a small pill so the highlight reads as a badge —
  // but only around a real value. Excel paints a row's empty cells too; on a
  // card a colored "—" reads as noise, and the left edge already carries the
  // row color.
  const showPill = value !== "—" && Boolean(cellStyle.backgroundColor)
  return (
    <div style={cardMetricStyle}>
      <span style={cardMetricLabelStyle}>{label}</span>
      <span
        style={
          showPill
            ? {
                ...cardMetricValueStyle,
                ...cellStyle,
                padding: "1px 5px",
                borderRadius: THEME.borderRadius.sm,
                alignSelf: "flex-start",
              }
            : cardMetricValueStyle
        }
      >
        {value}
      </span>
    </div>
  )
}

const cardListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
}

const cardListHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: THEME.spacing.sm,
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  padding: `0 2px`,
}

const cardStyle = (highlight: string | undefined): React.CSSProperties => ({
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  // The row's winning Excel highlight; silver when the row has none, so the
  // card anatomy stays constant and only the color carries meaning.
  borderLeft: `4px solid ${highlight ?? THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.s12}`,
  fontFamily: THEME.fonts.primary,
})

// The identity line and the car-name line share one size (Ian, 2026-10-11):
// the name shrank to the identity line's size rather than the reverse,
// because at the name's old 14px no phone-width line fits stock and VIN and
// every VIN was cut off.
const CARD_HEADER_FONT_SIZE = 11

const cardTitleRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: THEME.spacing.sm,
  marginBottom: THEME.spacing.xs,
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: CARD_HEADER_FONT_SIZE,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

// Chip shape matches the wholesale card's title-status chip
// (MatchResultsTable.tsx titleStatusChipStyle); background comes from the
// shot-status cell's own highlight (PARTIAL blue / Status-V orange), neutral
// when the cell carries none.
function shotStatusChipStyle(background: string | undefined): React.CSSProperties {
  return {
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: THEME.borderRadius.sm,
    background: background ?? THEME.colors.neutral.light.background,
    color: THEME.colors.neutral.light.textPrimary,
  }
}

// Same shape as shotStatusChipStyle; a light navy tint so ADDED reads apart
// from PARTIAL's blue (a hand-added car carries no shot status anyway).
const addedChipStyle: React.CSSProperties = {
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: THEME.borderRadius.sm,
  background: tint(THEME.colors.navy, 0.12),
  color: THEME.colors.navy,
}

// Bold and in the car name's color, with the card's divider (same as
// cardSummaryStyle's) under it separating it from the car name (Ian,
// 2026-10-11). The line rides on this element so a card with no stock or VIN
// gets no stray rule at its top.
const cardIdentityStyle: React.CSSProperties = {
  fontSize: CARD_HEADER_FONT_SIZE,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.mono,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  paddingBottom: THEME.spacing.xs,
  marginBottom: THEME.spacing.xs,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}

const cardSummaryStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  borderTop: `1px solid ${THEME.colors.silver}`,
  paddingTop: THEME.spacing.xs,
}

const cardMetricStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
}

const cardMetricLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const cardMetricValueStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textPrimary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}

const progressErrorStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.semantic.danger,
  marginBottom: THEME.spacing.xs,
}

const cardNotesRowStyle: React.CSSProperties = {
  marginTop: THEME.spacing.xs,
  cursor: "pointer",
  // A slightly generous hit area for thumbs without adding visual height.
  padding: "2px 0",
}

const cardNotesStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  fontStyle: "italic",
}

const cardAddNoteStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: THEME.colors.navy,
}
